import { useEffect, useState, useRef } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  Loader2,
  Plus,
  Download,
  Upload,
  Check,
  X,
  Pencil,
  ChevronLeft,
  ChevronRight,
  Search,
  ArrowRight,
} from 'lucide-react'
import { Label } from '@/shared/components/ui/label'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui/dialog'
import {
  useTranslationKeys,
  useLanguages,
  useCreateTranslationKey,
  useDeleteTranslationKey,
  useUpsertTranslationValue,
  useDeleteTranslationValue,
  useImportTranslations,
  type TranslationKeyWithValues,
} from '@/admin/hooks/use-translations'
import api from '@/admin/lib/api'

import { toastApiError } from '@/shared/lib/api-error'
type Scope = 'global' | 'tenant'

/**
 * `staff` marks the platform staff surface (/staff/settings/translations),
 * which alone may author global keys/values and target any tenant. The tenant
 * settings surface (/settings/translations) is locked to the viewer's own
 * tenant overrides. The server enforces this regardless; the prop only shapes
 * the UI to the caller's authority.
 */
export function TranslationsTab({ staff = false }: { staff?: boolean }) {
  const { t } = useTranslation()
  const [language, setLanguage] = useState('en')
  const [source, setSource] = useState<string>('')
  const [scope, setScope] = useState<Scope>(staff ? 'global' : 'tenant')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  // Tenant surface: "mine" = labels this org overrode; "all" = full catalogue.
  const [tenantTab, setTenantTab] = useState<'mine' | 'all'>('mine')
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false)

  const PAGE_SIZE = 50

  // Reset to the first page whenever the filters/search/tab change so results
  // never land the viewer on a now-out-of-range page.
  useEffect(() => {
    setPage(1)
  }, [search, source, language, scope, tenantTab])

  const { data: languages = [] } = useLanguages()
  const { data: keysPage, isLoading } = useTranslationKeys({
    source: source || undefined,
    search: search || undefined,
    language,
    page,
    page_size: PAGE_SIZE,
    // "Your wording" tab = only this org's overrides; "All labels" = everything.
    // New overrides are added through the guided dialog, not this list.
    overridden: !staff && tenantTab === 'mine' ? true : undefined,
  })
  const keys = keysPage?.results ?? []
  const totalCount = keysPage?.count ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  // Tab badges: dedicated count-only queries (page_size 1) so each badge is
  // exact and independent of the active tab/search. `count` is standard DRF
  // pagination, so this is robust where merged list-metadata was not.
  const { data: mineCountPage } = useTranslationKeys(
    { language, overridden: true, page_size: 1 },
    { enabled: !staff },
  )
  const { data: allCountPage } = useTranslationKeys(
    { language, page_size: 1 },
    { enabled: !staff },
  )
  const overriddenCount = mineCountPage?.count ?? 0
  const allLabelsCount = allCountPage?.count ?? 0

  const createKey = useCreateTranslationKey()
  const deleteKey = useDeleteTranslationKey()
  const upsertValue = useUpsertTranslationValue()
  const deleteValue = useDeleteTranslationValue()
  const importTranslations = useImportTranslations()

  // ── Export handler ──────────────────────────────────────
  async function handleExport() {
    try {
      const params: Record<string, string> = { language, scope }
      if (source) params.source = source
      const res = await api.get('/translations/export/', { params })
      const blob = new Blob([JSON.stringify(res.data, null, 2)], {
        type: 'application/json',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `translations-${language}-${scope}.json`
      a.click()
      URL.revokeObjectURL(url)
      toast.success(t('translations.exportSuccess'))
    } catch (err) {
      toastApiError(err, t('translations.exportError'));
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2 className="clara-title">{t('translations.title')}</h2>
        {/* Global key/value authoring + bulk file ops are staff-only. */}
        {staff ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setImportDialogOpen(true)}
              className="clara-btn-secondary flex items-center gap-1.5"
            >
              <Upload className="h-3.5 w-3.5" />
              {t('common.import')}
            </button>
            <button
              onClick={handleExport}
              className="clara-btn-secondary flex items-center gap-1.5"
            >
              <Download className="h-3.5 w-3.5" />
              {t('common.export')}
            </button>
            <button
              data-testid="translations-add-btn"
              onClick={() => setAddDialogOpen(true)}
              className="clara-btn flex items-center gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" />
              {t('translations.addKey')}
            </button>
          </div>
        ) : (
          // Tenant admins override existing keys' wording for their own org;
          // the guided dialog makes that action discoverable.
          <Button
            data-testid="translations-override-btn"
            onClick={() => setOverrideDialogOpen(true)}
            className="flex items-center gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('translations.overrideCta', 'Override a translation')}
          </Button>
        )}
      </div>
      {!staff && (
        <p className="clara-small mb-5 text-[var(--ink-3)]">
          {t(
            'translations.tenantIntro',
            'These are the labels your organisation has customised. Use Override a translation to change any other wording - everything else follows the iWarranty default.'
          )}
        </p>
      )}
      {/* Tenant tabs: what you've changed vs the full catalogue. */}
      {!staff && (
        <div className="mb-4 flex overflow-hidden rounded-md border-[0.5px] border-[var(--rule-strong)] w-fit">
          <button
            onClick={() => setTenantTab('mine')}
            className={`px-3.5 py-1.5 text-[11px] tabular-nums transition-colors ${
              tenantTab === 'mine'
                ? 'bg-[var(--green)] text-[var(--bg)]'
                : 'text-[var(--ink-2)] hover:bg-[var(--bg-subtle)]'
            }`}
          >
            {t('translations.colYourWording', 'Your wording')}
            <span className="ml-1.5 opacity-70">{overriddenCount}</span>
          </button>
          <button
            onClick={() => setTenantTab('all')}
            className={`border-l-[0.5px] border-l-[var(--rule-strong)] px-3.5 py-1.5 text-[11px] tabular-nums transition-colors ${
              tenantTab === 'all'
                ? 'bg-[var(--green)] text-[var(--bg)]'
                : 'text-[var(--ink-2)] hover:bg-[var(--bg-subtle)]'
            }`}
          >
            {t('translations.allLabels', 'All labels')}
            <span className="ml-1.5 opacity-70">{allLabelsCount}</span>
          </button>
        </div>
      )}
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="clara-select"
        >
          {languages.length === 0 && <option value="en">English</option>}
          {languages.map((l) => (
            <option key={l.code} value={l.code}>
              {l.name}
            </option>
          ))}
        </select>

        {/* FE/BE is an authoring distinction - only relevant on the staff surface. */}
        {staff && (
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="clara-select"
          >
            <option value="">{t('translations.allSources')}</option>
            <option value="FE">{t('translations.frontend')}</option>
            <option value="BE">{t('translations.backend')}</option>
          </select>
        )}

        {/* Only staff choose scope; tenant admins are locked to their own overrides. */}
        {staff && (
          <div className="flex overflow-hidden rounded-md border-[0.5px] border-[var(--rule)]">
            <button
              onClick={() => setScope('global')}
              className={`px-3 py-1.5 text-[11px] transition-colors ${
                scope === 'global'
                  ? 'bg-[var(--green)] text-[var(--bg)]'
                  : 'text-[var(--ink-2)]'
              }`}
            >
              {t('translations.global')}
            </button>
            <button
              onClick={() => setScope('tenant')}
              className={`border-l-[0.5px] border-l-[var(--rule)] px-3 py-1.5 text-[11px] transition-colors ${
                scope === 'tenant'
                  ? 'bg-[var(--green)] text-[var(--bg)]'
                  : 'text-[var(--ink-2)]'
              }`}
            >
              {t('translations.tenantOverride')}
            </button>
          </div>
        )}

        <input
          type="text"
          placeholder={t('translations.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="clara-input flex-1"
        />
      </div>
      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
        </div>
      ) : keys.length === 0 ? (
        <p className="clara-small py-12 text-center text-[var(--ink-3)]">
          {!staff && tenantTab === 'mine' && !search
            ? t(
                'translations.tenantEmpty',
                "Nothing overridden yet - use Override a translation above to set your organisation's wording for any label."
              )
            : t('translations.emptyState')}
        </p>
      ) : (
        <>
        <div className="max-h-[60vh] overflow-auto rounded-[8px] border-[0.5px] border-[var(--rule)]">
          <table className="w-full min-w-[600px]">
            <thead className="sticky top-0 z-10 bg-[var(--bg)]">
              <tr className="border-b-[0.5px] border-b-[var(--rule)]">
                <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-[var(--ink-3)]">
                  {staff ? t('common.key') : t('translations.colLabel', 'Label')}
                </th>
                {staff && (
                  <th className="w-16 px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-[var(--ink-3)]">
                    {t('common.source')}
                  </th>
                )}
                <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-[var(--ink-3)]">
                  {staff
                    ? t('translations.globalValue')
                    : t('translations.colDefault', 'iWarranty default')}
                </th>
                <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-[var(--ink-3)]">
                  {staff
                    ? t('translations.tenantOverride')
                    : t('translations.colYourWording', 'Your wording')}
                </th>
                {staff ? <th className="w-20" /> : <th className="w-28" />}
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <TranslationRow
                  key={k.id}
                  item={k}
                  language={language}
                  scope={scope}
                  staff={staff}
                  onSave={(value) => {
                    upsertValue.mutate(
                      {
                        key_id: k.id,
                        language_code: language,
                        tenant_uuid: scope === 'tenant' ? undefined : null,
                        value,
                      },
                      {
                        onSuccess: () => toast.success(t('translations.saved')),
                        onError: (err) => toastApiError(err, t('translations.errorSave')),
                      }
                    )
                  }}
                  onDelete={() => {
                    deleteKey.mutate(k.id, {
                      onSuccess: () => toast.success(t('translations.keyDeleted')),
                      onError: (err) => toastApiError(err, t('translations.errorDelete')),
                    })
                  }}
                  onReset={() => {
                    if (k.tenant_value_id == null) return
                    deleteValue.mutate(k.tenant_value_id, {
                      onSuccess: () =>
                        toast.success(t('translations.resetDone', 'Reset to default')),
                      onError: (err) => toastApiError(err, t('translations.errorSave')),
                    })
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination - search/filter is server-side, so it spans every page.
            Shown on both tabs for consistency, even when there is a single page. */}
        <div className="mt-3 flex items-center justify-between">
            <span className="text-[11px] text-[var(--ink-3)]">
              {t('translations.pageOf', {
                defaultValue: 'Page {{page}} of {{total}} ({{count}} keys)',
                page,
                total: totalPages,
                count: totalCount,
              })}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="flex items-center gap-1"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                {t('common.previous')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="flex items-center gap-1"
              >
                {t('common.next')}
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </>
      )}
      {/* Add Key Dialog */}
      {addDialogOpen && (
        <AddKeyDialog
          onClose={() => setAddDialogOpen(false)}
          onSubmit={(data) => {
            createKey.mutate(data, {
              onSuccess: () => {
                toast.success(t('translations.keyCreated'))
                setAddDialogOpen(false)
              },
              onError: (err) => toastApiError(err, t('translations.errorCreateKey')),
            })
          }}
          loading={createKey.isPending}
        />
      )}
      {/* Import Dialog */}
      {importDialogOpen && (
        <ImportDialog
          languages={languages}
          onClose={() => setImportDialogOpen(false)}
          onSubmit={(data) => {
            importTranslations.mutate(data, {
              onSuccess: (result) => {
                toast.success(
                  `${t('translations.importComplete')}: ${result.created} ${t('translations.created')}, ${result.updated} ${t('translations.updated')}`
                )
                setImportDialogOpen(false)
              },
              onError: (err) => toastApiError(err, t('translations.errorImport')),
            })
          }}
          loading={importTranslations.isPending}
        />
      )}
      {/* Override Dialog (tenant guided flow) */}
      <OverrideDialog
        open={overrideDialogOpen}
        language={language}
        onClose={() => setOverrideDialogOpen(false)}
        onSaved={() => {
          toast.success(t('translations.saved'))
          setOverrideDialogOpen(false)
        }}
      />
    </div>
  );
}

// ── Inline editable row ──────────────────────────────────

function TranslationRow({
  item,
  scope,
  staff,
  onSave,
  onDelete,
  onReset,
}: {
  item: TranslationKeyWithValues
  language: string
  scope: Scope
  staff: boolean
  onSave: (value: string) => void
  onDelete: () => void
  onReset: () => void
}) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState('')

  const activeValue = scope === 'tenant' ? item.tenant_value : item.global_value

  function startEdit() {
    setEditValue(activeValue ?? '')
    setEditing(true)
  }

  function save() {
    // Enter can bypass the disabled Save button - never persist a blank value.
    if (!editValue.trim()) return
    onSave(editValue)
    setEditing(false)
  }

  function cancel() {
    setEditing(false)
  }

  // ── Tenant "Your wording" row: the label's default -> this org's override,
  // read like an editorial correction. Always shows Edit / Reset. ──
  if (!staff) {
    return (
      <tr className="group border-b-[0.5px] border-b-[var(--rule-light)] transition-colors hover:bg-[var(--bg-light)]">
        <td className="px-4 py-3.5 align-top">
          {item.description ? (
            <>
              <span className="block text-[13px] text-[var(--ink-1)]">
                {item.description}
              </span>
              <span className="mt-1 block font-mono text-[11px] text-[var(--ink-3)]">
                {item.key}
              </span>
            </>
          ) : (
            <span className="font-mono text-[12px] text-[var(--ink-1)]">
              {item.key}
            </span>
          )}
        </td>
        <td className="px-4 py-3.5 align-top">
          <span
            className="block max-w-[240px] truncate text-[13px] text-[var(--ink-3)]"
            title={item.global_value ?? ''}
          >
            {item.global_value ?? '-'}
          </span>
        </td>
        <td className="px-4 py-3.5 align-top">
          {editing ? (
            <div className="flex items-center gap-1">
              <input
                autoFocus
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') save()
                  if (e.key === 'Escape') cancel()
                }}
                className="clara-input flex-1 text-[13px] py-1"
              />
              <Button
                variant="ghost"
                onClick={save}
                className="p-1 hover:bg-[var(--bg-subtle)] rounded"
              >
                <Check className="h-3.5 w-3.5 text-[var(--green)]" />
              </Button>
              <Button
                variant="ghost"
                onClick={cancel}
                className="p-1 hover:bg-[var(--bg-subtle)] rounded"
              >
                <X className="h-3.5 w-3.5 text-[var(--ink-3)]" />
              </Button>
            </div>
          ) : item.tenant_value != null ? (
            <div className="flex items-center gap-2">
              <ArrowRight className="h-3.5 w-3.5 flex-none text-[var(--green)]" />
              <span className="h-[5px] w-[5px] flex-none rounded-full bg-[var(--green)]" />
              <button
                onClick={startEdit}
                className="text-left text-[13px] text-[var(--ink-1)] hover:underline"
                title={item.tenant_value ?? ''}
              >
                {item.tenant_value}
              </button>
            </div>
          ) : (
            // "All labels" tab: this label still uses the default - offer to set one.
            <Button
              variant="outline"
              size="xs"
              onClick={startEdit}
              className="flex items-center gap-1 text-[11px] text-[var(--ink-2)]"
            >
              <Pencil className="h-3 w-3" />
              {t('translations.setOverride', 'Set override')}
            </Button>
          )}
        </td>
        <td className="px-4 py-3.5 text-right align-top">
          {!editing && item.tenant_value != null && (
            <div className="flex justify-end gap-1 opacity-70 transition-opacity group-hover:opacity-100">
              <Button
                variant="ghost"
                size="xs"
                onClick={startEdit}
                className="rounded px-2 py-1 text-[11px] font-normal text-[var(--ink-3)] hover:bg-[var(--bg-subtle)] hover:text-[var(--ink-1)]"
              >
                {t('common.edit', 'Edit')}
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={onReset}
                className="rounded px-2 py-1 text-[11px] font-normal text-[var(--ink-3)] hover:bg-[var(--bg-subtle)] hover:text-[var(--ink-1)]"
                title={t('translations.resetHint', 'Remove your override and use the default')}
              >
                {t('translations.reset', 'Reset')}
              </Button>
            </div>
          )}
        </td>
      </tr>
    )
  }

  return (
    <tr
      className="border-b-[0.5px] border-b-[var(--rule)] transition-colors hover:bg-[var(--bg-subtle)]"
    >
      <td className="px-4 py-2.5">
        <span className="text-[12px] font-mono text-[var(--ink-1)]">
          {item.key}
        </span>
        {item.description && (
          <span className="mt-0.5 block text-[10px] text-[var(--ink-3)]">
            {item.description}
          </span>
        )}
      </td>
      <td className="px-4 py-2.5">
        <span
          className="rounded bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--ink-2)]"
        >
          {item.source}
        </span>
      </td>
      <td className="px-4 py-2.5">
        {staff && scope === 'global' && editing ? (
          <div className="flex items-center gap-1">
            <input
              autoFocus
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') save()
                if (e.key === 'Escape') cancel()
              }}
              className="clara-input flex-1 text-[12px] py-1"
            />
            <Button variant="ghost" onClick={save} className="p-1 hover:bg-[var(--bg-subtle)] rounded">
              <Check className="h-3.5 w-3.5 text-[var(--green)]" />
            </Button>
            <Button variant="ghost" onClick={cancel} className="p-1 hover:bg-[var(--bg-subtle)] rounded">
              <X className="h-3.5 w-3.5 text-[var(--ink-3)]" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-1 group">
            <span className={`block max-w-[200px] truncate text-[12px] ${item.global_value ? 'text-[var(--ink-2)]' : 'text-[var(--ink-3)]'}`} title={item.global_value ?? ''}>
              {item.global_value ?? '-'}
            </span>
            {staff && scope === 'global' && (
              <Button
                variant="ghost"
                onClick={startEdit}
                className="p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[var(--bg-subtle)] rounded"
              >
                <Pencil className="h-3 w-3 text-[var(--ink-3)]" />
              </Button>
            )}
          </div>
        )}
      </td>
      <td className="px-4 py-2.5">
        {scope === 'tenant' && editing ? (
          <div className="flex items-center gap-1">
            <input
              autoFocus
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') save()
                if (e.key === 'Escape') cancel()
              }}
              className="clara-input flex-1 text-[12px] py-1"
            />
            <Button variant="ghost" onClick={save} className="p-1 hover:bg-[var(--bg-subtle)] rounded">
              <Check className="h-3.5 w-3.5 text-[var(--green)]" />
            </Button>
            <Button variant="ghost" onClick={cancel} className="p-1 hover:bg-[var(--bg-subtle)] rounded">
              <X className="h-3.5 w-3.5 text-[var(--ink-3)]" />
            </Button>
          </div>
        ) : scope !== 'tenant' ? (
          // Read-only reference of the tenant column while in global scope.
          <span className={`text-[12px] ${item.tenant_value ? 'text-[var(--ink-2)]' : 'text-[var(--ink-3)]'}`}>
            {item.tenant_value ?? t('translations.usingGlobalDefault')}
          </span>
        ) : item.tenant_value ? (
          <div className="flex items-center gap-1">
            <span className="text-[12px] text-[var(--ink-2)]">{item.tenant_value}</span>
            <Button
              variant="ghost"
              onClick={startEdit}
              className="p-1 hover:bg-[var(--bg-subtle)] rounded"
            >
              <Pencil className="h-3 w-3 text-[var(--ink-3)]" />
            </Button>
          </div>
        ) : (
          // No override yet - an always-visible affordance so it is obvious the
          // value can be customised (the pencil was previously hover-only).
          <Button
            variant="outline"
            size="xs"
            onClick={startEdit}
            className="flex items-center gap-1 text-[11px] text-[var(--ink-2)]"
          >
            <Pencil className="h-3 w-3" />
            {t('translations.setOverride', 'Set override')}
          </Button>
        )}
      </td>
      {/* Deleting a key is a global structural change - staff only. */}
      {staff && (
        <td className="px-4 py-2.5 text-right">
          <button
            onClick={onDelete}
            className="text-[11px] px-2 py-1 rounded transition-colors text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]"
          >
            {t('common.delete')}
          </button>
        </td>
      )}
    </tr>
  );
}

// ── Add Key Dialog ───────────────────────────────────────

function AddKeyDialog({
  onClose,
  onSubmit,
  loading,
}: {
  onClose: () => void
  onSubmit: (data: { key: string; description: string; source: string }) => void
  loading: boolean
}) {
  const { t } = useTranslation()
  const [key, setKey] = useState('')
  const [description, setDescription] = useState('')
  const [source, setSource] = useState('FE')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div
        className="w-full max-w-md rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-6"
      >
        <h3 className="clara-title mb-4">{t('translations.addKeyTitle')}</h3>

        <div className="space-y-3">
          <div>
            <Label className="clara-label">{t('common.key')}</Label>
            <input
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder={t('translations.keyPlaceholder')}
              className="clara-input w-full"
            />
          </div>
          <div>
            <Label className="clara-label">{t('common.description')}</Label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('translations.descriptionPlaceholder')}
              className="clara-input w-full"
            />
          </div>
          <div>
            <Label className="clara-label">{t('common.source')}</Label>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="clara-select w-full"
            >
              <option value="FE">{t('translations.frontend')}</option>
              <option value="BE">{t('translations.backend')}</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="ghost" onClick={onClose} className="clara-btn-secondary">
            {t('common.cancel')}
          </Button>
          <Button
            data-testid="translations-dialog-submit"
            onClick={() => onSubmit({ key, description, source })}
            disabled={!key || loading}
            className="clara-btn flex items-center gap-1.5"
          >
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t('common.create')}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Import Dialog ────────────────────────────────────────

function ImportDialog({
  languages,
  onClose,
  onSubmit,
  loading,
}: {
  languages: { code: string; name: string }[]
  onClose: () => void
  onSubmit: (data: {
    language_code: string
    source: string
    tenant_uuid?: string | null
    data: Record<string, string>
  }) => void
  loading: boolean
}) {
  const { t } = useTranslation()
  const [languageCode, setLanguageCode] = useState('en')
  const [source, setSource] = useState('FE')
  const [scope, setScope] = useState<Scope>('global')
  const [fileData, setFileData] = useState<Record<string, string> | null>(null)
  const [fileName, setFileName] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result as string)
        setFileData(parsed)
      } catch (err) {
        toastApiError(err, t('translations.invalidJson'));
        setFileData(null)
      }
    }
    reader.readAsText(file)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div
        className="w-full max-w-md rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-6"
      >
        <h3 className="clara-title mb-4">{t('translations.importTitle')}</h3>

        <div className="space-y-3">
          <div>
            <Label className="clara-label">{t('common.language')}</Label>
            <select
              value={languageCode}
              onChange={(e) => setLanguageCode(e.target.value)}
              className="clara-select w-full"
            >
              {languages.length === 0 && <option value="en">English</option>}
              {languages.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="clara-label">{t('common.source')}</Label>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="clara-select w-full"
            >
              <option value="FE">{t('translations.frontend')}</option>
              <option value="BE">{t('translations.backend')}</option>
            </select>
          </div>
          <div>
            <Label className="clara-label">{t('translations.scope')}</Label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value as Scope)}
              className="clara-select w-full"
            >
              <option value="global">{t('translations.global')}</option>
              <option value="tenant">{t('translations.tenantOverride')}</option>
            </select>
          </div>
          <div>
            <Label className="clara-label">{t('translations.jsonFile')}</Label>
            <input
              ref={fileRef}
              type="file"
              accept=".json"
              onChange={handleFile}
              className="hidden"
            />
            <button
              onClick={() => fileRef.current?.click()}
              className="clara-btn-secondary w-full flex items-center justify-center gap-1.5"
            >
              <Upload className="h-3.5 w-3.5" />
              {fileName || t('translations.chooseFile')}
            </button>
            {fileData && (
              <p className="mt-1 text-[11px] text-[var(--ink-3)]">
                {Object.keys(fileData).length} {t('translations.keysFound')}
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="ghost" onClick={onClose} className="clara-btn-secondary">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (!fileData) return
              onSubmit({
                language_code: languageCode,
                source,
                tenant_uuid: scope === 'tenant' ? undefined : null,
                data: fileData,
              })
            }}
            disabled={!fileData || loading}
            className="clara-btn flex items-center gap-1.5"
          >
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {t('common.import')}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Override Dialog (tenant guided flow) ─────────────────
//
// A discoverable path for tenant admins: search an existing key, see its
// global default, and set the wording their organisation should see. Tenant
// admins never create keys (staff-only), so this is search-and-select over the
// existing catalogue; the server forces the write to the caller's own tenant.

function OverrideDialog({
  open,
  language,
  onClose,
  onSaved,
}: {
  open: boolean
  language: string
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const [term, setTerm] = useState('')
  const [selected, setSelected] = useState<TranslationKeyWithValues | null>(null)
  const [value, setValue] = useState('')
  const upsertValue = useUpsertTranslationValue()

  useEffect(() => {
    if (open) {
      setTerm('')
      setSelected(null)
      setValue('')
    }
  }, [open])

  const { data: keysPage, isFetching } = useTranslationKeys({
    search: term || undefined,
    language,
    page_size: 20,
  })
  const results = keysPage?.results ?? []

  function choose(k: TranslationKeyWithValues) {
    setSelected(k)
    setValue(k.tenant_value ?? '')
  }

  function save() {
    // Enter can bypass the disabled Save button - require a non-blank value.
    if (!selected || !value.trim()) return
    upsertValue.mutate(
      {
        key_id: selected.id,
        language_code: language,
        tenant_uuid: undefined, // server forces the caller's own tenant
        value,
      },
      {
        onSuccess: onSaved,
        onError: (err) => toastApiError(err, t('translations.errorSave')),
      }
    )
  }

  if (!open) return null

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('translations.overrideCta', 'Override a translation')}</DialogTitle>
          <DialogDescription>
            {t(
              'translations.overrideDialogHint',
              'Search by the words you see in your portal, not the technical key.'
            )}
          </DialogDescription>
        </DialogHeader>

        {!selected ? (
          <div className="space-y-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--ink-3)]" />
              <Input
                autoFocus
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder={t('translations.searchKeyPlaceholder', 'Type what you see - e.g. Categories')}
                className="pl-8"
              />
            </div>
            {/* Fixed height so the dialog never resizes as results change. */}
            <div className="h-[320px] overflow-auto rounded-[8px] border-[0.5px] border-[var(--rule)]">
              {isFetching && results.length === 0 ? (
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin text-[var(--ink-3)]" />
                </div>
              ) : results.length === 0 ? (
                <p className="clara-small flex h-full items-center justify-center px-4 text-center text-[var(--ink-3)]">
                  {t('translations.emptyState')}
                </p>
              ) : (
                results.map((k) => (
                  <Button
                    key={k.id}
                    variant="ghost"
                    onClick={() => choose(k)}
                    className="flex h-auto w-full flex-col items-start gap-1 rounded-none border-b-[0.5px] border-b-[var(--rule)] px-3 py-2.5 text-left last:border-b-0"
                  >
                    {/* Value-first: lead with the words the admin recognises. */}
                    <span className="text-[13px] text-[var(--ink-1)]">
                      {k.global_value ?? k.key}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="font-mono text-[10.5px] text-[var(--ink-3)]">{k.key}</span>
                      {k.tenant_value && (
                        <span className="text-[9.5px] uppercase tracking-[0.12em] text-[var(--green)]">
                          {t('translations.overriddenTag', 'Overridden')}
                        </span>
                      )}
                    </span>
                  </Button>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="clara-label">{t('common.key')}</Label>
              <p className="font-mono text-[12px] text-[var(--ink-1)]">{selected.key}</p>
            </div>
            <div>
              <Label className="clara-label">{t('translations.globalValue')}</Label>
              <p className="text-[12px] text-[var(--ink-2)]">{selected.global_value ?? '-'}</p>
            </div>
            <div>
              <Label className="clara-label">{t('translations.tenantOverride')}</Label>
              <Input
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') save()
                }}
                placeholder={selected.global_value ?? ''}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {selected ? (
            <>
              <Button variant="ghost" onClick={() => setSelected(null)}>
                {t('common.back')}
              </Button>
              <Button
                onClick={save}
                disabled={!value || upsertValue.isPending}
                className="flex items-center gap-1.5"
              >
                {upsertValue.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {t('common.save')}
              </Button>
            </>
          ) : (
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
